import { Plus, Settings2, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import {
  engineOverrideToPromptData,
  fallbackPromptData,
  normalizeLabel,
  promptDataToEngineOverride,
} from "../modelComparisonTypes";

import {
  BRAND_VISIBILITY_DEFAULT_QUERY_COUNT,
  BrandVisibilityModelSelection,
  BrandVisibilityPresetSubmitPayload,
  makeBrandVisibilityModelId,
} from "./brandVisibilityTypes";

import { ModelParameters } from "@/components/ModelParameters";
import { ProviderIcon } from "@/components/ProviderWithLogo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  buildBrandVisibilityStarterQueries,
  parseBrandVisibilityQueries,
} from "@/routes/smart-table/utils/brandVisibilityQueries";
import { PromptBlueprint } from "@/types";

interface BrandVisibilityPresetSetupProps {
  isSubmitting: boolean;
  onSubmit: (payload: BrandVisibilityPresetSubmitPayload) => void;
}

const NEW_MODEL_EDITOR_ID = "__new_brand_visibility_model__";

const parameterSummary = (parameters?: Record<string, unknown>) => {
  if (!parameters) {
    return null;
  }

  const enabledParameters = Object.entries(parameters).filter(([, value]) => {
    if (value === null || value === undefined || value === "") {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  });

  if (!enabledParameters.length) {
    return null;
  }

  return enabledParameters
    .slice(0, 2)
    .map(([key, value]) => `${key}: ${typeof value === "object" ? "custom" : String(value)}`)
    .join(" · ");
};

const modelDetails = (selection: BrandVisibilityModelSelection) => {
  const provider = selection.engine.provider ?? "Custom provider";
  const summary = parameterSummary(selection.engine.parameters);

  return summary ? `${provider} · ${summary}` : provider;
};

const normalizeModelPayload = (models: BrandVisibilityModelSelection[]) => {
  const counts = new Map<string, number>();

  return models
    .filter(selection => Boolean(selection.engine.model?.trim()))
    .map(selection => {
      const normalized =
        normalizeLabel(selection.label) || normalizeLabel(selection.engine.model) || "Model";
      const nextCount = (counts.get(normalized) ?? 0) + 1;
      counts.set(normalized, nextCount);

      return {
        label: nextCount === 1 ? normalized : `${normalized} ${nextCount}`,
        engine: selection.engine,
      };
    });
};

const initialStarterCount = String(BRAND_VISIBILITY_DEFAULT_QUERY_COUNT);

const BrandVisibilityPresetSetup = ({
  isSubmitting,
  onSubmit,
}: BrandVisibilityPresetSetupProps) => {
  const [domain, setDomain] = useState("");
  const [queriesText, setQueriesText] = useState("");

  const [starterTopic, setStarterTopic] = useState("");
  const [starterAudience, setStarterAudience] = useState("");
  const [starterCount, setStarterCount] = useState(initialStarterCount);

  const [models, setModels] = useState<BrandVisibilityModelSelection[]>(() => {
    const fallbackEngine = promptDataToEngineOverride(fallbackPromptData());

    return [
      {
        id: makeBrandVisibilityModelId("default"),
        label: fallbackEngine.model || "Model",
        engine: fallbackEngine,
      },
    ];
  });

  const [editingModelId, setEditingModelId] = useState<string | null>(null);
  const [editingPromptData, setEditingPromptData] = useState<
    Partial<PromptBlueprint> | undefined
  >();

  const parsedQueries = useMemo(() => parseBrandVisibilityQueries(queriesText), [queriesText]);
  const normalizedModels = useMemo(() => normalizeModelPayload(models), [models]);

  const trimmedDomain = domain.trim();
  const hasDomainError = !trimmedDomain;
  const hasQueriesError = parsedQueries.length < 1;
  const hasModelsError = normalizedModels.length < 1;

  const canSubmit = Boolean(
    trimmedDomain && parsedQueries.length > 0 && normalizedModels.length > 0,
  );

  const openNewModelEditor = () => {
    setEditingModelId(NEW_MODEL_EDITOR_ID);
    setEditingPromptData(fallbackPromptData());
  };

  const openEditModelEditor = (model: BrandVisibilityModelSelection) => {
    setEditingModelId(model.id);
    setEditingPromptData(engineOverrideToPromptData(model.engine));
  };

  const closeEditor = () => {
    setEditingModelId(null);
    setEditingPromptData(undefined);
  };

  const saveEditor = () => {
    if (!editingModelId || !editingPromptData) {
      return;
    }

    const engine = promptDataToEngineOverride(editingPromptData);
    if (!engine.model.trim()) {
      return;
    }

    if (editingModelId === NEW_MODEL_EDITOR_ID) {
      setModels(previous => [
        ...previous,
        {
          id: makeBrandVisibilityModelId(),
          label: engine.model,
          engine,
        },
      ]);
      closeEditor();
      return;
    }

    setModels(previous =>
      previous.map(model => {
        if (model.id !== editingModelId) {
          return model;
        }

        const shouldUpdateLabel =
          normalizeLabel(model.label) === normalizeLabel(model.engine.model);

        return {
          ...model,
          label: shouldUpdateLabel ? engine.model : model.label,
          engine,
        };
      }),
    );

    closeEditor();
  };

  const removeModel = (id: string) => {
    setModels(previous => previous.filter(model => model.id !== id));
  };

  const updateModelLabel = (id: string, label: string) => {
    setModels(previous => previous.map(model => (model.id === id ? { ...model, label } : model)));
  };

  const handleGenerateStarterQueries = () => {
    const parsedCount = Number(starterCount);
    const generated = buildBrandVisibilityStarterQueries({
      topic: starterTopic,
      audience: starterAudience,
      count: Number.isFinite(parsedCount) ? parsedCount : BRAND_VISIBILITY_DEFAULT_QUERY_COUNT,
    });

    if (!generated.length) {
      return;
    }

    const existing = parseBrandVisibilityQueries(queriesText);
    const nextQueries = parseBrandVisibilityQueries([...existing, ...generated].join("\n"));
    setQueriesText(nextQueries.join("\n"));
  };

  const selectedEngineModelName = promptDataToEngineOverride(editingPromptData).model;

  return (
    <div className="flex min-h-0 flex-col">
      <div className="max-h-[58vh] space-y-4 overflow-y-auto px-4 py-4 sm:px-5">
        <div className="space-y-1.5">
          <div className="flex items-start justify-between gap-3">
            <label className="text-sm font-medium text-gray-950" htmlFor="brand-visibility-domain">
              Domain or brand
            </label>
            <span className="rounded-md bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-400">
              Required
            </span>
          </div>
          <Input
            id="brand-visibility-domain"
            className="h-8 border-gray-200 bg-white text-sm shadow-none focus-visible:ring-1"
            value={domain}
            onChange={event => setDomain(event.target.value)}
            placeholder="e.g. promptlayer.com or PromptLayer"
            aria-invalid={hasDomainError}
          />
          {hasDomainError && <p className="text-xs text-red-600">Enter a domain or brand.</p>}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
            Starter queries
          </p>
          <div className="mt-2 space-y-1.5">
            <div className="grid gap-1.5 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs text-gray-500" htmlFor="brand-visibility-starter-topic">
                  Topic
                </label>
                <Input
                  id="brand-visibility-starter-topic"
                  className="h-8 border-gray-200 bg-white text-sm shadow-none focus-visible:ring-1"
                  value={starterTopic}
                  onChange={event => setStarterTopic(event.target.value)}
                  placeholder="e.g. AI observability"
                />
              </div>
              <div className="space-y-1">
                <label
                  className="text-xs text-gray-500"
                  htmlFor="brand-visibility-starter-audience"
                >
                  Audience (optional)
                </label>
                <Input
                  id="brand-visibility-starter-audience"
                  className="h-8 border-gray-200 bg-white text-sm shadow-none focus-visible:ring-1"
                  value={starterAudience}
                  onChange={event => setStarterAudience(event.target.value)}
                  placeholder="e.g. startup founders"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
              <div className="space-y-1 sm:w-28">
                <label className="text-xs text-gray-500" htmlFor="brand-visibility-starter-count">
                  Count
                </label>
                <Input
                  id="brand-visibility-starter-count"
                  type="number"
                  min={1}
                  max={25}
                  className="h-8 border-gray-200 bg-white text-sm shadow-none focus-visible:ring-1"
                  value={starterCount}
                  onChange={event => setStarterCount(event.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-8"
                onClick={handleGenerateStarterQueries}
                disabled={!starterTopic.trim()}
              >
                Add starter queries
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-start justify-between gap-3">
            <label className="text-sm font-medium text-gray-950" htmlFor="brand-visibility-queries">
              Queries
            </label>
            <span className="rounded-md bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-400">
              {parsedQueries.length} query{parsedQueries.length === 1 ? "" : "ies"}
            </span>
          </div>
          <Textarea
            id="brand-visibility-queries"
            className="min-h-[126px] resize-none border-gray-200 bg-white text-sm shadow-none focus-visible:ring-1"
            value={queriesText}
            onChange={event => setQueriesText(event.target.value)}
            placeholder={
              "One query per line\nBest AI app observability tools\nPromptLayer alternatives\nPromptLayer pricing"
            }
            aria-invalid={hasQueriesError}
          />
          {hasQueriesError && (
            <p className="text-xs text-red-600">Add at least one query (one per line).</p>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-medium text-gray-950">Models</p>
            <span className="rounded-md bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-400">
              {normalizedModels.length} model{normalizedModels.length === 1 ? "" : "s"}
            </span>
          </div>

          <div className="space-y-1.5">
            {models.map(model => (
              <div
                key={model.id}
                className="group rounded-lg border border-gray-200 bg-white px-3 py-2.5 transition-colors hover:border-gray-300"
              >
                <div className="flex items-center gap-2.5">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-50 ring-1 ring-gray-100">
                    <ProviderIcon provider={model.engine.provider ?? ""} className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Input
                      className="h-6 border-0 bg-transparent px-0 text-sm font-medium text-gray-950 shadow-none focus-visible:ring-0"
                      value={model.label}
                      onChange={event => updateModelLabel(model.id, event.target.value)}
                      aria-label="Model label"
                    />
                    <p className="truncate text-xs leading-4 text-gray-500">
                      {modelDetails(model)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-gray-400 hover:text-gray-900"
                    onClick={() => openEditModelEditor(model)}
                    aria-label="Edit model"
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-gray-400 hover:text-gray-900"
                    onClick={() => removeModel(model.id)}
                    aria-label="Remove model"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={openNewModelEditor}
              className="flex h-10 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-gray-200 text-sm text-gray-500 transition hover:border-gray-400 hover:bg-gray-50 hover:text-gray-800"
            >
              <Plus className="h-3.5 w-3.5" />
              Add model
            </button>
          </div>

          {hasModelsError && <p className="text-xs text-red-600">Add at least one model.</p>}
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className="text-xs text-gray-500">
            {canSubmit ? "Ready to create." : "Complete required fields to continue."}
          </p>
          <Button
            type="button"
            onClick={() => {
              if (!canSubmit) {
                return;
              }

              onSubmit({
                domain: trimmedDomain,
                queries: parsedQueries,
                models: normalizedModels,
              });
            }}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? "Creating…" : "Create and run eval"}
          </Button>
        </div>
      </div>

      <Dialog open={Boolean(editingModelId)} onOpenChange={isOpen => !isOpen && closeEditor()}>
        <DialogContent className="max-h-[88vh] w-[calc(100vw-1rem)] max-w-2xl overflow-y-auto p-0 sm:w-[calc(100vw-2rem)]">
          <div className="border-b border-gray-100 px-4 py-4 sm:px-6 sm:py-5">
            <DialogHeader>
              <DialogTitle className="text-base font-semibold text-gray-950">
                {editingModelId === NEW_MODEL_EDITOR_ID ? "Add model" : "Edit model"}
              </DialogTitle>
            </DialogHeader>
          </div>

          <div className="px-4 py-4 sm:px-6 sm:py-5">
            <ModelParameters
              className="w-full justify-between"
              isPromptChat={false}
              displayAsPopover={false}
              shouldShowClearButton={false}
              promptData={editingPromptData}
              onUpdate={setEditingPromptData}
            />
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3 sm:px-6 sm:py-4">
            <Button type="button" variant="ghost" onClick={closeEditor}>
              Cancel
            </Button>
            <Button type="button" onClick={saveEditor} disabled={!selectedEngineModelName.trim()}>
              Save model
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandVisibilityPresetSetup;
